import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus,
  Pencil,
  Trash2,
  Waypoints,
  Loader2,
  AlertCircle,
  Boxes,
  Microscope,
  FlaskConical,
  Dna,
  HeartPulse,
  TestTubes,
  Printer,
  Cable,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  integrationsApi,
  type Integration,
  type IntegrationCategory,
} from '@/api/integrationsApi'
import { sourcesApi, type Source } from '@/api/sourcesApi'
import { useTenant } from '@/providers/TenantProvider'
import { TopologyDiagram } from '@/components/integrations/TopologyDiagram'
import { ManageSourcesPanel } from '@/components/integrations/ManageSourcesPanel'
import { WebhooksPanel } from '@/components/integrations/WebhooksPanel'
import {
  SimulationPanel,
  isSimMode,
  type SimMode,
} from '@/components/integrations/simulation/SimulationPanel'
import {
  PrintersPanel,
  isPrintersView,
  type PrintersView,
} from '@/components/integrations/printers/PrintersPanel'
import { cn } from '@/lib/utils'
import { PageHeading } from '@/components/layout/PageHeading'
import { LisReviewButton } from '@/components/lis-review/LisReviewButton'

const CATEGORY_ICON: Record<IntegrationCategory, React.ElementType> = {
  instrument: Microscope,
  immunoassay: FlaskConical,
  cell_counter: Dna,
  ehr: HeartPulse,
  lis: TestTubes,
  printer: Printer,
  other: Boxes,
}

const CATEGORY_LABEL: Record<IntegrationCategory, string> = {
  instrument: 'Instrument',
  immunoassay: 'Immunoassay',
  cell_counter: 'Cell counter',
  ehr: 'EHR',
  lis: 'LIS',
  printer: 'Printer',
  other: 'Other',
}

const CATEGORY_ORDER: IntegrationCategory[] = [
  'instrument',
  'immunoassay',
  'cell_counter',
  'ehr',
  'lis',
  'printer',
  'other',
]

const TAB_VALUES = ['list', 'diagram', 'incoming', 'webhooks', 'sources', 'printers']

// Links shared before the rename still say ?tab=simulation, and an unknown value
// falls back to `list`, so without this they would quietly land on a tab nobody
// meant to share. Read-only: setTab never writes the old slug.
const TAB_ALIASES: Record<string, string> = { simulation: 'incoming' }

export function IntegrationsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Tab is URL-driven (?tab=) so it survives refresh and is deep-linkable.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const aliased = tabParam ? (TAB_ALIASES[tabParam] ?? tabParam) : null
  const tab = aliased && TAB_VALUES.includes(aliased) ? aliased : 'list'
  const setTab = (v: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', v)
    // `sim` only means anything under Incoming; drop it on the way out so a
    // stale ?tab=webhooks&sim=send can never linger in a shared URL.
    if (v !== 'incoming') next.delete('sim')
    // Same for Printers' list choice.
    if (v !== 'printers') next.delete('printers')
    setSearchParams(next, { replace: true })
  }

  // Incoming's sub-mode, same allowlist idiom as the tab above.
  const simParam = searchParams.get('sim')
  const sim: SimMode = isSimMode(simParam) ? simParam : 'receive'
  const setSim = (v: SimMode) => {
    const next = new URLSearchParams(searchParams)
    next.set('sim', v)
    setSearchParams(next, { replace: true })
  }

  // Printers' list (z8nrz7evkc), same allowlist idiom.
  const printersParam = searchParams.get('printers')
  const printersView: PrintersView = isPrintersView(printersParam) ? printersParam : 'isbt-templates'
  const setPrintersView = (v: PrintersView) => {
    const next = new URLSearchParams(searchParams)
    next.set('printers', v)
    setSearchParams(next, { replace: true })
  }
  const { selectedTenantId } = useTenant()

  const { data: sources = [] } = useQuery<Source[]>({
    queryKey: ['sources'],
    queryFn: sourcesApi.list,
  })

  const {
    data: integrations = [],
    isLoading: loading,
    error,
  } = useQuery<Integration[]>({
    queryKey: ['integrations', selectedTenantId],
    queryFn: () => integrationsApi.list(selectedTenantId as string),
    enabled: !!selectedTenantId,
  })

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<IntegrationCategory | 'all'>('all')
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const sourceName = useMemo(() => {
    const byId = new Map(sources.map((s) => [s.id, s.name]))
    return (id: string) => byId.get(id) ?? id
  }, [sources])

  // Categories present in the data, in canonical order, for the filter row.
  const presentCategories = useMemo(() => {
    const set = new Set(integrations.map((i) => i.category))
    return CATEGORY_ORDER.filter((c) => set.has(c))
  }, [integrations])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return integrations.filter((i) => {
      if (categoryFilter !== 'all' && i.category !== categoryFilter) return false
      if (!q) return true
      return i.name.toLowerCase().includes(q) || sourceName(i.sourceId).toLowerCase().includes(q)
    })
  }, [integrations, categoryFilter, search, sourceName])

  async function handleDelete(integration: Integration) {
    if (!selectedTenantId) return
    setDeleteError(null)
    try {
      await integrationsApi.remove(selectedTenantId, integration.id)
      queryClient.invalidateQueries({ queryKey: ['integrations', selectedTenantId] })
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete.')
    } finally {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="mx-auto max-w-screen-2xl px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Cable className="h-5 w-5" />
          </div>
          <PageHeading title='Integrations' description='Connections and printers for this tenant.' />
        </div>
        <div className="flex items-center gap-2">
          <LisReviewButton />
          <Button
            onClick={() => navigate('/integrations/new')}
            disabled={!selectedTenantId || sources.length === 0}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Integration
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error instanceof Error ? error.message : 'Failed to load integrations.'}
        </div>
      )}
      {deleteError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {deleteError}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="list">Integrations</TabsTrigger>
          <TabsTrigger value="diagram">Diagram</TabsTrigger>
          <TabsTrigger value="incoming">Incoming</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="sources">Manage Sources</TabsTrigger>
          <TabsTrigger value="printers">Printers</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search by name or source…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            {presentCategories.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <FilterChip
                  active={categoryFilter === 'all'}
                  onClick={() => setCategoryFilter('all')}
                  label="All"
                />
                {presentCategories.map((c) => (
                  <FilterChip
                    key={c}
                    active={categoryFilter === c}
                    onClick={() => setCategoryFilter(c)}
                    label={CATEGORY_LABEL[c]}
                  />
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !selectedTenantId ? (
            <EmptyState
              title="No tenant selected"
              body="Pick a tenant from the switcher in the top bar. Tenants are created in Configure > Tenants."
            />
          ) : integrations.length === 0 ? (
            <EmptyState
              title="No integrations yet"
              body="Add one from a source blueprint, or load the demo set with pnpm db:seed."
              action={
                <Button onClick={() => navigate('/integrations/new')} disabled={sources.length === 0}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Integration
                </Button>
              }
            />
          ) : filtered.length === 0 ? (
            <EmptyState title="No matches" body="No integrations match your search or filter." />
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  sourceName={sourceName(integration.sourceId)}
                  onOpen={() => navigate(`/integrations/${integration.id}`)}
                  onMap={() => navigate(`/integrations/${integration.id}?tab=mappings`)}
                  onEdit={() => navigate(`/integrations/${integration.id}?tab=edit`)}
                  onDelete={() => setDeleteTarget(integration)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="diagram">
          <TopologyDiagram />
        </TabsContent>

        <TabsContent value="incoming">
          <SimulationPanel mode={sim} onModeChange={setSim} />
        </TabsContent>

        <TabsContent value="webhooks">
          <WebhooksPanel tenantId={selectedTenantId} />
        </TabsContent>

        <TabsContent value="sources">
          <ManageSourcesPanel />
        </TabsContent>

        <TabsContent value="printers">
          <PrintersPanel view={printersView} onViewChange={setPrintersView} />
        </TabsContent>
      </Tabs>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove integration</DialogTitle>
            <DialogDescription>
              Remove <strong>{deleteTarget?.name}</strong>? This also deletes its stored credentials
              and any field mappings configured for it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteTarget && handleDelete(deleteTarget)}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-muted-foreground hover:bg-muted',
      )}
    >
      {label}
    </button>
  )
}

function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body: string
  action?: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-dashed py-16 text-center">
      <p className="text-sm font-medium">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{body}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  )
}

function IntegrationCard({
  integration,
  sourceName,
  onOpen,
  onMap,
  onEdit,
  onDelete,
}: {
  integration: Integration
  sourceName: string
  onOpen: () => void
  onMap: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const Icon = CATEGORY_ICON[integration.category] ?? Boxes
  const active = integration.status === 'active'
  return (
    <Card className="group border-border/60 transition-colors hover:border-border">
      <CardContent className="flex items-center gap-3 p-3.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
          <Icon className="h-5 w-5" />
        </div>
        <button onClick={onOpen} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium group-hover:underline">
              {integration.name}
            </span>
            <span
              className={cn(
                'h-1.5 w-1.5 shrink-0 rounded-full',
                active ? 'bg-green-500' : 'bg-muted-foreground/30',
              )}
              title={integration.status}
            />
          </div>
          <p className="truncate text-xs text-muted-foreground">{sourceName}</p>
        </button>
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onMap} title="Map to form field">
            <Waypoints className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} title="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            title="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
